import type { Schemas } from '../../../api/client';
import { describeChanges, type DiffRow } from './auditChanges';

export type SyncConflict = Schemas['SyncConflict'];
export type Device = Schemas['Device'];

/** The schema types these payloads as empty records; they are free-form objects. */
type Payloads = { serverSnapshot?: object; clientPayload?: object };

/** Field-by-field comparison of what the server has and what the device sent. */
export function conflictDiff(c: Payloads): DiffRow[] {
  const view = describeChanges({ before: c.serverSnapshot ?? {}, after: c.clientPayload ?? {} });
  return view.kind === 'diff' ? view.rows : [];
}

/** A starting point for a merge: the server's record with the device's values on top. */
export function mergeStart(c: Payloads): string {
  return JSON.stringify({ ...(c.serverSnapshot ?? {}), ...(c.clientPayload ?? {}) }, null, 2);
}

/** Parses merged data typed by the user; it must be a JSON object. */
export function parseMerged(
  text: string,
): { ok: true; value: Record<string, unknown> } | { ok: false; error: string } {
  let v: unknown;
  try {
    v = JSON.parse(text);
  } catch {
    return { ok: false, error: 'This is not valid JSON' };
  }
  if (typeof v !== 'object' || v === null || Array.isArray(v))
    return { ok: false, error: 'Merged data must be a JSON object { … }' };
  return { ok: true, value: v as Record<string, unknown> };
}

export type Health = { tone: 'success' | 'warning' | 'error' | 'neutral'; label: string };

/** A device's sync health from last contact, recent errors and open conflicts. */
export function deviceHealth(
  d: Pick<Device, 'revoked' | 'lastSeenAt'> & { errorsLast24h?: number; openConflicts?: number },
  now: number = Date.now(),
): Health {
  if (d.revoked) return { tone: 'neutral', label: 'Revoked' };
  if (!d.lastSeenAt) return { tone: 'warning', label: 'Never synced' };
  if ((d.openConflicts ?? 0) > 0) return { tone: 'warning', label: 'Needs review' };
  if ((d.errorsLast24h ?? 0) > 0) return { tone: 'error', label: 'Errors' };
  const hours = (now - new Date(d.lastSeenAt).getTime()) / 3_600_000;
  if (hours > 72) return { tone: 'warning', label: 'Offline' };
  return { tone: 'success', label: 'Healthy' };
}

export const RESOLUTION_LABEL: Record<SyncConflict['status'], string> = {
  OPEN: 'Open',
  RESOLVED_CLIENT: 'Kept device version',
  RESOLVED_SERVER: 'Kept server version',
  RESOLVED_MERGED: 'Merged',
};

/** "contact.update" → "Contact update". */
export function commandLabel(commandType: string | null | undefined): string {
  if (!commandType) return 'Unknown command';
  const s = commandType.replace(/[._]/g, ' ').toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}
